import React from 'react';
import { Modal } from 'react-bootstrap';
import {Button} from "antd";
import {CopyOutlined} from '@ant-design/icons'
import {toast} from "react-toastify";
import { keyFromJsonLine, raisedTheAlert } from './highlight';
import './LogDataModal.css';

export interface SensorValuesProps {
    logData: any;
    /** Component sensors that raised the alert, as trigger_pre_req entries. */
    highlightKeys?: Array<string>;
    /** Diagnostics code color: red, darkred, orange, yellow, green, purple, grey. */
    alertColor?: string;
    /** Diagnostics code shown above the JSON, e.g. R-3. */
    alertCode?: string;
    /** Heading and Copy button. Hidden inside the health-alert Data tab. */
    showToolbar?: boolean;
}

const HIGHLIGHT_COLORS = ['grey', 'green', 'purple', 'yellow', 'orange', 'red', 'darkred'];

const roundDecimals = (data: any, decimals = 2): any => {
    if (typeof data === 'number') {
      return Number(data.toFixed(decimals));
    } else if (Array.isArray(data)) {
      return data.map(item => roundDecimals(item, decimals));
    } else if (data && typeof data === 'object') {
      return Object.fromEntries(
        Object.entries(data).map(([key, value]) => [key, roundDecimals(value, decimals)])
      );
    }
    return data;
  };

export const AlertSensorValues: React.FC<SensorValuesProps> = ({ logData, highlightKeys, alertColor, alertCode, showToolbar = true }) => {
    const cleanData = roundDecimals(logData, 2);
    const JsonData = JSON.stringify(cleanData, null, 2) || ''
    const copyToClipboard = () => {
        navigator.clipboard.writeText(JsonData)
            .then(() => {
                toast.success("JSON Copied to Clipboard",{autoClose:1000})
            })
            .catch(err => console.error('Failed to copy text: ', err));
    }

    const highlights = highlightKeys || []
    const colorClass = alertColor && HIGHLIGHT_COLORS.includes(alertColor) ? alertColor : 'red'

    return (
        <div className="row">
            <div className="col-md-12">
                <div className="form-group mb-0">
                    {showToolbar && (
                    <div>
                        <label className="custom-label col-md-10">
                            {alertCode ?
                                <>
                                    <span className={`${colorClass}-status`}>{alertCode}</span>
                                    {' - Alert Sensor Values'}
                                </>
                                : 'Alert Sensor Values'
                            }
                        </label>
                        <Button className="" type="default" size="small" onClick={copyToClipboard}>
                            <CopyOutlined />
                            Copy
                        </Button>
                    </div>
                    )}
                    <pre className="json-view">
                        {JsonData.split('\n').map((line, index) => {
                            const key = keyFromJsonLine(line)
                            const highlighted = highlights.length > 0
                                && raisedTheAlert(key, highlights)
                            return (
                                <div
                                    key={index}
                                    className={highlighted
                                        ? `json-line json-line-highlight json-line-${colorClass}`
                                        : 'json-line'}
                                >
                                    {line}
                                </div>
                            )
                        })}
                    </pre>
                </div>
            </div>
        </div>
    );
}

export interface Props extends SensorValuesProps {
    sensorBoxName: string;
    isVisible: boolean;
    onClose: () => void;
}

const LogDataModal: React.FC<Props> = ({ isVisible, sensorBoxName, logData, onClose, highlightKeys, alertColor, alertCode }) => {
    return (
        <Modal
            className="logs-modal-body"
            show={isVisible}
            onHide={onClose}
        >
            <Modal.Header closeButton>
                {sensorBoxName}
            </Modal.Header>
            <Modal.Body>
                <AlertSensorValues
                    logData={logData}
                    highlightKeys={highlightKeys}
                    alertColor={alertColor}
                    alertCode={alertCode}
                />
            </Modal.Body>
        </Modal>
    );
}

export default LogDataModal
